import { useMemo, useState } from "react";

import clsx from "clsx";
import moment from "moment";

import { Dropdown } from "@components/v2/inputs/select/dropdown";
import { OptionItem } from "@components/v2/inputs/select/option-item";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { useFeatureFlags } from "@hooks/http";
import {
  todayLocalAsISODate,
  type DateRangeChoiceV3,
  type FixedDateInterval,
} from "@north-beam/nb-common";
import { parseDateRangeArgument } from "@utils/index";

import { DATE_RANGE_CHOICES } from "../constants";

import { DatePicker } from "./date-picker";

interface Props {
  dateRange: FixedDateInterval | DateRangeChoiceV3;
  minDate: string;
  onChange: (value: FixedDateInterval | DateRangeChoiceV3) => void;
}

export const DateSelect = ({ dateRange, minDate, onChange }: Props) => {
  const { hasFeatureFlag } = useFeatureFlags();

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const dateRangeOptions = useMemo(() => {
    return DATE_RANGE_CHOICES.map((e) => {
      const minLimitDate = moment(minDate);
      const startRange = moment().subtract(e.amount, e.unit);

      return {
        value: e.value,
        label: e.label,
        disabled: minLimitDate.isSameOrAfter(startRange),
      };
    });
  }, [minDate]);

  const value = useMemo(() => {
    if (typeof dateRange === "string") {
      const option = DATE_RANGE_CHOICES.find((e) => e.value === dateRange);
      return option?.value ?? "custom";
    } else {
      return "custom";
    }
  }, [dateRange]);

  const label = useMemo(() => {
    if (typeof dateRange === "string") {
      const option = DATE_RANGE_CHOICES.find((e) => e.value === dateRange);
      return option?.labelForSelect ?? "";
    } else {
      const daysCount =
        moment(dateRange.endDate).diff(dateRange.startDate, "days") + 1;
      return `${daysCount} days`;
    }
  }, [dateRange]);

  return (
    <>
      {typeof dateRange !== "string" && isCalendarOpen ? (
        <DatePicker
          minDate={minDate}
          dateRange={dateRange}
          label={label}
          onClose={() => setIsCalendarOpen(false)}
          onChange={(val) => onChange(val)}
        />
      ) : (
        <Listbox
          value={value}
          onChange={(val) => {
            if (val === "custom") {
              if (typeof dateRange === "string") {
                const dateRangeInterval = parseDateRangeArgument(
                  dateRange,
                  todayLocalAsISODate(),
                );

                onChange({
                  startDate: dateRangeInterval.startDate,
                  endDate: dateRangeInterval.endDate,
                  type: "fixed",
                });
              }

              setIsCalendarOpen(true);
            } else {
              onChange(val);
            }
          }}
        >
          {typeof dateRange === "string" && <span>the</span>}
          <ListboxButton
            className={clsx(
              "group font-semibold inline-flex items-center gap-1",
              "hover:text-v2-action data-[open]:text-v2-action",
            )}
          >
            <span className="underline">{label}</span>
            <i
              className={clsx(
                "fa-regular fa-angle-down text-base",
                "group-data-[open]:text-v2-action group-data-[open]:rotate-180",
                "transition-[transform] duration-200 ease-out",
              )}
            />
          </ListboxButton>
          <ListboxOptions
            as={Dropdown}
            anchor={{ to: "bottom start", gap: 4 }}
            transition
            className={clsx("w-28", {
              "bg-white": !hasFeatureFlag("enableDashboardV2"),
            })}
          >
            {dateRangeOptions.map((option) => {
              return (
                <ListboxOption
                  as={OptionItem}
                  key={String(option.value)}
                  size={"sm"}
                  value={option.value}
                  option={option}
                  disabled={option.disabled}
                  className="static"
                />
              );
            })}
          </ListboxOptions>
        </Listbox>
      )}
    </>
  );
};
